import type {
  CampaignAnalyticsGranularity,
  CampaignAnalyticsResponse,
  DashboardAiSummary,
  SalesTrafficResponse,
} from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { PerformancePanel } from "@/components/shared/performance/PerformancePanel";
import {
  buildDashboardPerformance,
  DASHBOARD_PERFORMANCE_METRICS,
  type DashboardPerformanceMetric,
  DEFAULT_DASHBOARD_PERFORMANCE_SLOTS,
} from "@/lib/dashboard-sales";
import { AiDashboardSections } from "./ai-dashboard-sections";

interface DashboardOverviewSectionsProps {
  analytics: CampaignAnalyticsResponse | undefined;
  salesTraffic?: SalesTrafficResponse;
  salesLoading?: boolean;
  salesFetching?: boolean;
  salesError?: boolean;
  onSalesRetry?: () => void;
  startDate: string;
  endDate: string;
  metric: DashboardPerformanceMetric;
  compareMetric: DashboardPerformanceMetric | null;
  granularity: CampaignAnalyticsGranularity;
  onMetricChange: (metric: DashboardPerformanceMetric) => void;
  onCompareMetricChange: (metric: DashboardPerformanceMetric | null) => void;
  onGranularityChange: (granularity: CampaignAnalyticsGranularity) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Inline CTA shown above the Sales retry when Ads is disconnected. */
  adsNotice?: React.ReactNode;
  summary?: DashboardAiSummary;
}

/** One shared chart, independently loaded Ads and Seller sources, then AI sections. */
export function DashboardOverviewSections({
  analytics,
  salesTraffic,
  salesLoading = false,
  salesFetching = false,
  salesError = false,
  onSalesRetry,
  startDate,
  endDate,
  metric,
  compareMetric,
  granularity,
  onMetricChange,
  onCompareMetricChange,
  onGranularityChange,
  isLoading,
  isFetching,
  isError,
  onRetry,
  adsNotice,
  summary,
}: DashboardOverviewSectionsProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const { t: campaignT } = useTranslation("campaign");
  const model = buildDashboardPerformance({
    analytics,
    salesTraffic,
    startDate,
    endDate,
    granularity,
  });
  const metricLabel = (key: DashboardPerformanceMetric) =>
    ["totalSales", "tacos", "spend", "sales", "orders", "acos"].includes(key)
      ? t(`salesTraffic.labels.${key}`)
      : campaignT(`performance.metricMenu.${key}`);
  const kpis = model.kpis.map((kpi) => ({
    ...kpi,
    loading: kpi.key === "totalSales" || kpi.key === "tacos" ? salesLoading : isLoading,
  }));
  // Sales and Ads load independently, and HTTP/schema failures get no toast, so the
  // chart keeps one compact retry for Sales without reviving the diagnostics footer.
  const salesNotice = salesError ? (
    <div role="alert">
      <ErrorState
        variant="inline"
        message={t("salesTraffic.loadFailed")}
        onRetry={onSalesRetry}
        isRetrying={salesFetching}
      />
    </div>
  ) : undefined;
  const notice =
    adsNotice || salesNotice ? (
      <div className="space-y-2">
        {adsNotice}
        {salesNotice}
      </div>
    ) : undefined;
  return (
    <>
      <PerformancePanel<DashboardPerformanceMetric>
        title={t("salesTraffic.title")}
        subtitle={t("salesTraffic.window", { startDate, endDate })}
        metrics={DASHBOARD_PERFORMANCE_METRICS}
        defaultSlots={DEFAULT_DASHBOARD_PERFORMANCE_SLOTS}
        kpis={kpis}
        trend={model.trend}
        dataThrough={analytics?.dataThrough ?? salesTraffic?.dataThrough ?? null}
        metric={metric}
        compareMetric={compareMetric}
        granularity={granularity}
        onMetricChange={onMetricChange}
        onCompareMetricChange={onCompareMetricChange}
        onGranularityChange={onGranularityChange}
        getMetricLabel={metricLabel}
        isLoading={isLoading && salesLoading}
        isFetching={isFetching || salesFetching}
        disableInteractionsWhileFetching={false}
        showIncompleteNotice={false}
        isError={isError}
        onRetry={onRetry}
        notice={notice}
        collapseStorageKey="adpilot:dashboard-performance:v1"
        slotsStorageKey="adpilot:dashboard-performance-slots:v2"
        size="compact"
      />
      {summary ? <AiDashboardSections summary={summary} /> : null}
    </>
  );
}
